import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useIsMobileSm } from '../hooks/useMediaQuery';
import { getIstDateIso } from '../utils/istTime';

function parseDateIso(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDateIso(date) {
  return format(date, 'yyyy-MM-dd');
}

function NutritionCalendar({
  recordedSet,
  selectedDate,
  onSelect,
  viewMonth,
  onViewMonthChange,
  maxDateIso,
}) {
  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
    <div className="nutrition-calendar">
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          className="inline-flex min-h-touch min-w-touch items-center justify-center rounded-lg border border-border text-text hover:bg-surface"
          onClick={() => onViewMonthChange(subMonths(viewMonth, 1))}
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <p className="text-sm font-semibold text-text">{format(viewMonth, 'MMMM yyyy')}</p>
        <button
          type="button"
          className="inline-flex min-h-touch min-w-touch items-center justify-center rounded-lg border border-border text-text hover:bg-surface"
          onClick={() => onViewMonthChange(addMonths(viewMonth, 1))}
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-muted">
        {weekDays.map((day) => (
          <div key={day} className="py-1">
            {day}
          </div>
        ))}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-1">
        {days.map((day) => {
          const iso = formatDateIso(day);
          const hasData = recordedSet.has(iso);
          const isSelected = selectedDate && isSameDay(day, parseDateIso(selectedDate));
          const inMonth = isSameMonth(day, viewMonth);
          const isFuture = iso > maxDateIso;
          const disabled = !hasData || isFuture || !inMonth;

          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(iso)}
              className={[
                'min-h-[2.25rem] rounded-lg text-sm font-medium transition-colors',
                !inMonth && 'opacity-0 pointer-events-none',
                disabled && inMonth && 'cursor-not-allowed text-muted opacity-35',
                hasData && !isSelected && 'bg-primary-light text-primary hover:bg-primary/20',
                isSelected && 'bg-primary text-white ring-2 ring-primary/40',
                !hasData && inMonth && !isFuture && 'text-muted',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function NutritionDatePicker({
  dates = [],
  selectedDate,
  onSelect,
  labels,
  id = 'nutrition-date-picker',
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobileSm();
  const [viewMonth, setViewMonth] = useState(() =>
    selectedDate ? parseDateIso(selectedDate) : new Date()
  );

  const recordedSet = useMemo(() => {
    const set = new Set(dates.map((d) => String(d).slice(0, 10)));
    if (selectedDate) {
      set.add(String(selectedDate).slice(0, 10));
    }
    return set;
  }, [dates, selectedDate]);
  const maxDateIso = getIstDateIso();
  const hasCalendarDates = dates.length > 0;
  const canPickDate = hasCalendarDates || Boolean(selectedDate);

  useEffect(() => {
    if (selectedDate) {
      setViewMonth(parseDateIso(selectedDate));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open, isMobile]);

  useEffect(() => {
    if (!open || !isMobile) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, isMobile]);

  const handleSelect = (iso) => {
    onSelect(iso);
    setOpen(false);
  };

  const calendar = (
    <NutritionCalendar
      recordedSet={recordedSet}
      selectedDate={selectedDate}
      onSelect={handleSelect}
      viewMonth={viewMonth}
      onViewMonthChange={setViewMonth}
      maxDateIso={maxDateIso}
    />
  );

  const mobileSheet =
    open &&
    canPickDate &&
    isMobile &&
    createPortal(
      <>
        <button
          type="button"
          className="fixed inset-0 z-[80] bg-black/45"
          aria-label="Close"
          onClick={() => setOpen(false)}
        />
        <div className="fixed inset-x-0 bottom-0 z-[90] max-h-[85vh] overflow-y-auto rounded-t-2xl border border-border bg-card p-4 pb-safe shadow-card">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-text">{labels.date}</p>
            <button
              type="button"
              className="inline-flex min-h-touch min-w-touch items-center justify-center rounded-lg text-muted hover:bg-surface hover:text-text"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {calendar}
        </div>
      </>,
      document.body
    );

  return (
    <div ref={rootRef} className="relative space-y-2">
      <label htmlFor={id} className="block text-sm font-medium text-text">
        {labels.date}
      </label>
      <button
        id={id}
        type="button"
        disabled={!canPickDate}
        onClick={() => canPickDate && setOpen((prev) => !prev)}
        className="flex w-full min-h-touch items-center justify-between gap-2 rounded-lg border border-border bg-input px-3 py-2.5 text-left text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className={selectedDate ? 'font-medium' : 'text-muted'}>
          {selectedDate || labels.selectDatePrompt}
        </span>
        <Calendar className="h-4 w-4 shrink-0 text-muted" aria-hidden />
      </button>

      {open && canPickDate && !isMobile && (
        <div className="absolute left-0 right-0 z-50 mt-1 rounded-xl border border-border bg-card p-3 shadow-card">
          {calendar}
        </div>
      )}

      {mobileSheet}

      {hasCalendarDates ? (
        <p className="text-xs leading-relaxed text-muted">{labels.nutritionDatesHighlightHint}</p>
      ) : selectedDate ? (
        <p className="text-xs leading-relaxed text-muted">{labels.nutritionTodayOnlyHint}</p>
      ) : (
        <p className="text-sm text-muted">{labels.nutritionNoHistory}</p>
      )}
    </div>
  );
}
